import {useEffect, useMemo, useRef} from 'react';
import {I18nextProvider, useTranslation} from 'react-i18next';

import type {GeneralSaleTransactionType, TrustPurpose} from '@sollapay/enums';
import type {i18n as I18nInstance, Namespace, TFunction} from 'i18next';
import type {FC, MutableRefObject, ReactNode} from 'react';

export interface DealTypeProviderProps {
  purpose: TrustPurpose | null;
  transactionType?: GeneralSaleTransactionType | null;
  children: ReactNode;
}

interface DealTypeState {
  purpose: TrustPurpose | null;
  transactionType: GeneralSaleTransactionType | null;
}

/**
 * Scopes every `useTranslation` and `<Trans>` beneath it to a deal type, so `key_general_sale`
 * wins over `key` wherever an override exists — no per-component opt-in.
 */
export const DealTypeProvider: FC<DealTypeProviderProps> = ({
  purpose,
  transactionType = null,
  children
}) => {
  const {i18n: base} = useTranslation();
  const stateRef = useRef<DealTypeState>({purpose, transactionType});
  const scoped = useMemo(() => (purpose ? scopedInstance(base, stateRef) : null), [base, purpose]);

  useEffect(() => {
    const prev = stateRef.current;
    if (prev.purpose === purpose && prev.transactionType === transactionType) return;
    stateRef.current = {purpose, transactionType};

    // react-i18next's `useTranslation` caches its snapshot per mounted component (ready/lng/
    // keyPrefix/revision) and never notices `getFixedT`'s own override changing underneath it —
    // only a `languageChanged` emission busts that cache. Switching deal type on an already-mounted
    // account (transactionType is changeable post-creation, unlike trustPurpose) must nudge every
    // already-mounted `useTranslation` beneath this provider without swapping the `i18n` instance
    // identity (that would force a full remount, dropping any open modal's local state).
    scoped?.emit('languageChanged', scoped.language);
  }, [scoped, purpose, transactionType]);

  useEffect(() => {
    if (!scoped) return;
    const onLanguageChanged = (language: string) => void scoped.changeLanguage(language);
    base.on('languageChanged', onLanguageChanged);
    return () => base.off('languageChanged', onLanguageChanged);
  }, [base, scoped]);

  if (!scoped) return <>{children}</>;

  return <I18nextProvider i18n={scoped}>{children}</I18nextProvider>;
};

export interface DealTypeTranslation {
  t: TFunction;
  i18n: I18nInstance;
}

/**
 * For the handful of surfaces that know their deal type but render outside a DealTypeProvider —
 * list rows, and toasts that mount in a portal at the app root.
 */
export function useDealTypeTranslation(
  ns: Namespace,
  purpose: TrustPurpose | null,
  transactionType: GeneralSaleTransactionType | null = null
): DealTypeTranslation {
  const {t, i18n} = useTranslation(ns);
  const dealTypeT = useMemo(
    () => bindDealType(t, purpose, transactionType),
    [t, purpose, transactionType]
  );

  return {t: dealTypeT, i18n};
}

export function bindDealType(
  t: TFunction,
  purpose: TrustPurpose | null,
  transactionType: GeneralSaleTransactionType | null = null
): TFunction {
  const bound = (key: string | string[], options?: Record<string, unknown>) => {
    // `<Trans>` always passes `context`, usually undefined — an explicit one wins outright.
    if (options?.context !== undefined || !purpose) return t(key, options);

    // i18next falls back `key_<context>` → `key` with no middle step, so resolve the chain here.
    const withOverrides = (k: string): string[] =>
      transactionType
        ? [`${k}_${purpose}_${transactionType}`, `${k}_${purpose}`, k]
        : [`${k}_${purpose}`, k];

    // Callers may pass an i18next fallback-key array (e.g. a short/full label pair) — expand
    // each key's own override chain in place so priority stays per-key, not flattened wrong.
    const keys = Array.isArray(key) ? key.flatMap(withOverrides) : withOverrides(key as string);

    return t(keys, options);
  };

  // `<Trans>` picks its namespace from `t.ns`, falling back to defaultNS when absent.
  return Object.assign(bound, t) as unknown as TFunction;
}

/**
 * One clone per mounted `DealTypeProvider`, sharing the base store and following its language.
 * `getFixedT`'s override reads `stateRef.current` at call time rather than closing over a fixed
 * deal type, so the same instance keeps serving already-mounted consumers across a transaction-
 * type switch — only the emitted `languageChanged` event needs to change, not the instance itself.
 */
function scopedInstance(
  base: I18nInstance,
  stateRef: MutableRefObject<DealTypeState>
): I18nInstance {
  const clone = base.cloneInstance();
  const getFixedT = clone.getFixedT.bind(clone);
  clone.getFixedT = ((...args: Parameters<typeof getFixedT>) =>
    bindDealType(
      getFixedT(...args),
      stateRef.current.purpose,
      stateRef.current.transactionType
    )) as typeof clone.getFixedT;

  return clone;
}
